import { DateTime } from 'luxon';
import React, { useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { Link } from 'react-router-dom';

import { useToast } from 'Component/Alerta';
import Botao from 'Component/Botao';
import Form, { Assinatura, CaixaDeTexto, Radio } from 'Component/Form';
import Imagem from 'Component/Imagem';
import Modal from 'Component/Modal';
import AvaliacaoExperiencia from 'Interface/AvaliacoesExperiencias';
import VinculoEmpregaticio from 'Interface/VinculosEmpregaticios';
import {
  useAdicionarAvaliacaoExperienciaMutation,
  useEditarAvaliacaoExperienciaMutation,
  useRecuperarAvaliacoesExperienciasQuery,
} from 'View/Service/Default/AvaliacoesExperiencias';

interface Props {
  dados: VinculoEmpregaticio;
}

const Lista = ({ dados }: Props): React.ReactElement => {
  const toast = useToast();
  const [modal, setModal] = useState<boolean>(false);
  const [adicionarAvaliacao] = useAdicionarAvaliacaoExperienciaMutation();
  const [editarAvaliacao] = useEditarAvaliacaoExperienciaMutation();

  const {
    data: { avaliacoes: [avaliacao] } = {
      avaliacoes: [{} as AvaliacaoExperiencia],
    },
  } = useRecuperarAvaliacoesExperienciasQuery({
    options: { usuario_id: dados.usuario_id },
  });

  const enviar = async (dadosForm: AvaliacaoExperiencia): Promise<void> => {
    try {
      if (typeof dadosForm.assinatura_funcionario_45 === 'string') {
        delete dadosForm.assinatura_funcionario_45;
      }
      if (typeof dadosForm.assinatura_funcionario_90 === 'string') {
        delete dadosForm.assinatura_funcionario_90;
      }
      if (typeof dadosForm.assinatura_superior_45 === 'string') {
        delete dadosForm.assinatura_superior_45;
      }
      if (typeof dadosForm.assinatura_superior_90 === 'string') {
        delete dadosForm.assinatura_superior_90;
      }
      if (typeof dadosForm.assinatura_diretor_45 === 'string') {
        delete dadosForm.assinatura_diretor_45;
      }
      if (typeof dadosForm.assinatura_diretor_90 === 'string') {
        delete dadosForm.assinatura_diretor_90;
      }
      if (typeof dadosForm.realizado_45 === 'string') {
        delete dadosForm.realizado_45;
      }
      if (typeof dadosForm.realizado_90 === 'string') {
        delete dadosForm.realizado_90;
      }

      if (avaliacao && avaliacao.id) {
        await editarAvaliacao(dadosForm).unwrap();

        toast({ conteudo: 'Editado com sucesso!', tema: 'success' });
      } else {
        await adicionarAvaliacao({
          ...dadosForm,
          vinculo_empregaticio_id: dados.id,
        }).unwrap();

        toast({ conteudo: 'Adicionado com sucesso!', tema: 'success' });
      }

      setModal(false);
    } catch (error) {
      console.log(error);
      toast({
        titulo: 'Erro ao adicionar',
        conteudo: 'Verifique os logs para maiores detalhes.',
        tema: 'danger',
      });
    }
  };

  return (
    <React.Fragment>
      <div className="d-flex border-bottom align-items-center py-2 px-3">
        <Link to={`/usuarios/editar/${dados.usuario_id}`}>
          <Imagem
            src={dados.usuario?.imagem}
            alt={dados.usuario?.nome}
            className="rounded-circle object-fit-cover"
            width="80"
            height="80"
          />
        </Link>
        <div className="ms-4 fs-4">
          <Link
            to={`/usuarios/editar/${dados.usuario_id}`}
            className="text-decoration-none"
          >
            {dados.usuario?.nome}
          </Link>
          <small className="d-block fs-6 text-muted">
            {dados.empresa?.nome_fantasia}
          </small>
        </div>
        <BrowserView className="d-flex flex-grow-1 align-items-center justify-content-end">
          <div className="ms-5 fs-5 text-end">
            <div className="fs-4">
              {dados.periodo_de_experiencia1 < 0
                ? 0
                : dados.periodo_de_experiencia1}
              <small className="d-block fs-6 text-muted">
                Prazo de 45 dias
              </small>
            </div>
          </div>
          <div className="ms-5 fs-5 text-end">
            <div className="fs-4">
              {dados.periodo_de_experiencia2 < 0
                ? 0
                : dados.periodo_de_experiencia2}
              <small className="d-block fs-6 text-muted">
                Prazo de 90 dias
              </small>
            </div>
          </div>
          <div className="ms-5 fs-5 text-end">
            <div className="fs-4">
              {dados.admissao.data().toLocaleString()}
              <small className="d-block fs-6 text-muted">Admissão em</small>
            </div>
          </div>
          <div className="ms-2 fs-5 text-end">
            <Botao
              icone="cloud-upload-fill"
              tema="success"
              className="ms-5"
              onClick={() => setModal(true)}
            />
          </div>
        </BrowserView>
      </div>
      <Modal lg exibir={modal} titulo="Avaliação" aoFechar={setModal}>
        <Form onSubmit={enviar} inicial={avaliacao}>
          <table className="table table-striped table-hover">
            <thead>
              <tr>
                <td>INDICADORES</td>
                <td>PERÍODO</td>
                <td>RUIM</td>
                <td>REGULAR</td>
                <td>BOM</td>
                <td>ÓTIMO</td>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td rowSpan={2}>
                  <strong>
                    CAPACIDADE PARA APRENDER:
                    <br />
                  </strong>
                  habilidade em reter / assimilar informações recebidas e
                  usá-las adequadamente.
                </td>
                <td>45 dias</td>
                <td>
                  <Radio name="dados.capacidade_45" value="Ruim" semMargem />
                </td>
                <td>
                  <Radio name="dados.capacidade_45" value="Regular" semMargem />
                </td>
                <td>
                  <Radio name="dados.capacidade_45" value="Bom" semMargem />
                </td>
                <td>
                  <Radio name="dados.capacidade_45" value="Ótimo" semMargem />
                </td>
              </tr>
              <tr>
                <td>90 dias</td>
                <td>
                  <Radio name="dados.capacidade_90" value="Ruim" semMargem />
                </td>
                <td>
                  <Radio name="dados.capacidade_90" value="Regular" semMargem />
                </td>
                <td>
                  <Radio name="dados.capacidade_90" value="Bom" semMargem />
                </td>
                <td>
                  <Radio name="dados.capacidade_90" value="Ótimo" semMargem />
                </td>
              </tr>
              <tr>
                <td rowSpan={2}>
                  <strong>
                    PRODUTIVIDADE:
                    <br />
                  </strong>
                  Ritmo de trabalho, aliado ao rendimento e qualidade com que o
                  funcionário desenvolve a tarefa.
                </td>
                <td>45 dias</td>
                <td>
                  <Radio name="dados.produtividade_45" value="Ruim" semMargem />
                </td>
                <td>
                  <Radio
                    name="dados.produtividade_45"
                    value="Regular"
                    semMargem
                  />
                </td>
                <td>
                  <Radio name="dados.produtividade_45" value="Bom" semMargem />
                </td>
                <td>
                  <Radio
                    name="dados.produtividade_45"
                    value="Ótimo"
                    semMargem
                  />
                </td>
              </tr>
              <tr>
                <td>90 dias</td>
                <td>
                  <Radio name="dados.produtividade_90" value="Ruim" semMargem />
                </td>
                <td>
                  <Radio
                    name="dados.produtividade_90"
                    value="Regular"
                    semMargem
                  />
                </td>
                <td>
                  <Radio name="dados.produtividade_90" value="Bom" semMargem />
                </td>
                <td>
                  <Radio
                    name="dados.produtividade_90"
                    value="Ótimo"
                    semMargem
                  />
                </td>
              </tr>
              <tr>
                <td rowSpan={2}>
                  <strong>
                    INICIATIVA:
                    <br />
                  </strong>
                  habilidade em agir / executar as tarefas e solucionar
                  problemas sem necessidade de supervisão constante.
                </td>
                <td>45 dias</td>
                <td>
                  <Radio name="dados.iniciativa_45" value="Ruim" semMargem />
                </td>
                <td>
                  <Radio name="dados.iniciativa_45" value="Regular" semMargem />
                </td>
                <td>
                  <Radio name="dados.iniciativa_45" value="Bom" semMargem />
                </td>
                <td>
                  <Radio name="dados.iniciativa_45" value="Ótimo" semMargem />
                </td>
              </tr>
              <tr>
                <td>90 dias</td>
                <td>
                  <Radio name="dados.iniciativa_90" value="Ruim" semMargem />
                </td>
                <td>
                  <Radio name="dados.iniciativa_90" value="Regular" semMargem />
                </td>
                <td>
                  <Radio name="dados.iniciativa_90" value="Bom" semMargem />
                </td>
                <td>
                  <Radio name="dados.iniciativa_90" value="Ótimo" semMargem />
                </td>
              </tr>
              <tr>
                <td rowSpan={2}>
                  <strong>
                    COLABORAÇÃO:
                    <br />
                  </strong>
                  disposição que o funcionário possui em ajudar a equipe e a
                  empresa, de uma forma geral, através de atitudes espontâneas.
                </td>
                <td>45 dias</td>
                <td>
                  <Radio name="dados.colaboracao_45" value="Ruim" semMargem />
                </td>
                <td>
                  <Radio
                    name="dados.colaboracao_45"
                    value="Regular"
                    semMargem
                  />
                </td>
                <td>
                  <Radio name="dados.colaboracao_45" value="Bom" semMargem />
                </td>
                <td>
                  <Radio name="dados.colaboracao_45" value="Ótimo" semMargem />
                </td>
              </tr>
              <tr>
                <td>90 dias</td>
                <td>
                  <Radio name="dados.colaboracao_90" value="Ruim" semMargem />
                </td>
                <td>
                  <Radio
                    name="dados.colaboracao_90"
                    value="Regular"
                    semMargem
                  />
                </td>
                <td>
                  <Radio name="dados.colaboracao_90" value="Bom" semMargem />
                </td>
                <td>
                  <Radio name="dados.colaboracao_90" value="Ótimo" semMargem />
                </td>
              </tr>
              <tr>
                <td rowSpan={2}>
                  <strong>
                    RELACIONAMENTO:
                    <br />
                  </strong>
                  habilidade no trato com as pessoas, independente do nível
                  hierárquico, influenciando positivamente e obtendo aceitação
                  pessoal.
                </td>
                <td>45 dias</td>
                <td>
                  <Radio
                    name="dados.relacionamento_45"
                    value="Ruim"
                    semMargem
                  />
                </td>
                <td>
                  <Radio
                    name="dados.relacionamento_45"
                    value="Regular"
                    semMargem
                  />
                </td>
                <td>
                  <Radio name="dados.relacionamento_45" value="Bom" semMargem />
                </td>
                <td>
                  <Radio
                    name="dados.relacionamento_45"
                    value="Ótimo"
                    semMargem
                  />
                </td>
              </tr>
              <tr>
                <td>90 dias</td>
                <td>
                  <Radio
                    name="dados.relacionamento_90"
                    value="Ruim"
                    semMargem
                  />
                </td>
                <td>
                  <Radio
                    name="dados.relacionamento_90"
                    value="Regular"
                    semMargem
                  />
                </td>
                <td>
                  <Radio name="dados.relacionamento_90" value="Bom" semMargem />
                </td>
                <td>
                  <Radio
                    name="dados.relacionamento_90"
                    value="Ótimo"
                    semMargem
                  />
                </td>
              </tr>
              <tr>
                <td rowSpan={2}>
                  <strong>
                    PONTUALIDADE / ASSIDUIDADE:
                    <br />
                  </strong>
                  cumprimento dos horários de trabalho e prazos para entrega de
                  tarefas.
                </td>
                <td>45 dias</td>
                <td>
                  <Radio name="dados.pontualidade_45" value="Ruim" semMargem />
                </td>
                <td>
                  <Radio
                    name="dados.pontualidade_45"
                    value="Regular"
                    semMargem
                  />
                </td>
                <td>
                  <Radio name="dados.pontualidade_45" value="Bom" semMargem />
                </td>
                <td>
                  <Radio name="dados.pontualidade_45" value="Ótimo" semMargem />
                </td>
              </tr>
              <tr>
                <td>90 dias</td>
                <td>
                  <Radio name="dados.pontualidade_90" value="Ruim" semMargem />
                </td>
                <td>
                  <Radio
                    name="dados.pontualidade_90"
                    value="Regular"
                    semMargem
                  />
                </td>
                <td>
                  <Radio name="dados.pontualidade_90" value="Bom" semMargem />
                </td>
                <td>
                  <Radio name="dados.pontualidade_90" value="Ótimo" semMargem />
                </td>
              </tr>
            </tbody>
          </table>

          <h5>Pontos fortes a destacas</h5>
          <div className="row">
            <div className="col-md-6">
              <CaixaDeTexto name="pontos_fortes_45" rotulo="45 dias" />
            </div>
            <div className="col-md-6">
              <CaixaDeTexto name="pontos_fortes_90" rotulo="90 dias" />
            </div>
          </div>

          <h5>Pontos a melhorar</h5>
          <div className="row">
            <div className="col-md-6">
              <CaixaDeTexto name="pontos_fracos_45" rotulo="45 dias" />
            </div>
            <div className="col-md-6">
              <CaixaDeTexto name="pontos_fracos_90" rotulo="90 dias" />
            </div>
          </div>

          <h5>Trajetória / Área de crescimento</h5>
          <CaixaDeTexto name="observacoes" rotulo="Observações" />

          <div className="row">
            <div className="col-md-6">
              <h5>45 dias</h5>
              {avaliacao && avaliacao.assinatura_funcionario_45 ? (
                <>
                  <Imagem
                    src={avaliacao.assinatura_funcionario_45}
                    className="img-fluid"
                  />
                  <h6 className="text-center">Assinatura do funcionário</h6>
                  <p className="text-center">
                    {avaliacao.realizado_45
                      ?.data()
                      .toLocaleString(DateTime.DATETIME_SHORT)}
                  </p>
                </>
              ) : (
                <Assinatura
                  name="assinatura_funcionario_45"
                  rotulo="Funcionário"
                />
              )}
              {avaliacao && avaliacao.assinatura_superior_45 ? (
                <>
                  <Imagem
                    src={avaliacao.assinatura_superior_45}
                    className="img-fluid"
                  />
                  <h6 className="text-center">Assinatura do superior</h6>
                  <p className="text-center">
                    {avaliacao.realizado_45
                      ?.data()
                      .toLocaleString(DateTime.DATETIME_SHORT)}
                  </p>
                </>
              ) : (
                <Assinatura name="assinatura_superior_45" rotulo="Superior" />
              )}
              {avaliacao && avaliacao.assinatura_diretor_45 ? (
                <>
                  <Imagem
                    src={avaliacao.assinatura_diretor_45}
                    className="img-fluid"
                  />
                  <h6 className="text-center">Assinatura do diretor</h6>
                  <p className="text-center">
                    {avaliacao.realizado_45
                      ?.data()
                      .toLocaleString(DateTime.DATETIME_SHORT)}
                  </p>
                </>
              ) : (
                <Assinatura name="assinatura_diretor_45" rotulo="Diretor" />
              )}
            </div>
            <div className="col-md-6">
              <h5>90 dias</h5>
              {avaliacao && avaliacao.assinatura_funcionario_90 ? (
                <>
                  <Imagem
                    src={avaliacao.assinatura_funcionario_90}
                    className="img-fluid"
                  />
                  <h6 className="text-center">Assinatura do funcionário</h6>
                  <p className="text-center">
                    {avaliacao.realizado_90
                      ?.data()
                      .toLocaleString(DateTime.DATETIME_SHORT)}
                  </p>
                </>
              ) : (
                <Assinatura
                  name="assinatura_funcionario_90"
                  rotulo="Funcionário"
                />
              )}
              {avaliacao && avaliacao.assinatura_superior_90 ? (
                <>
                  <Imagem
                    src={avaliacao.assinatura_superior_90}
                    className="img-fluid"
                  />
                  <h6 className="text-center">Assinatura do superior</h6>
                  <p className="text-center">
                    {avaliacao.realizado_90
                      ?.data()
                      .toLocaleString(DateTime.DATETIME_SHORT)}
                  </p>
                </>
              ) : (
                <Assinatura name="assinatura_superior_90" rotulo="Superior" />
              )}
              {avaliacao && avaliacao.assinatura_diretor_90 ? (
                <>
                  <Imagem
                    src={avaliacao.assinatura_diretor_90}
                    className="img-fluid"
                  />
                  <h6 className="text-center">Assinatura do diretor</h6>
                  <p className="text-center">
                    {avaliacao.realizado_90
                      ?.data()
                      .toLocaleString(DateTime.DATETIME_SHORT)}
                  </p>
                </>
              ) : (
                <Assinatura name="assinatura_diretor_90" rotulo="Diretor" />
              )}
            </div>
          </div>

          <div>
            <Botao type="submit">Enviar</Botao>{' '}
            <Botao tema="light" onClick={() => setModal(false)}>
              Cancelar
            </Botao>
          </div>
        </Form>
      </Modal>
    </React.Fragment>
  );
};

export default Lista;
